import React, {useState, useEffect, useRef, useReducer} from "react";
import MDEditor from "@uiw/react-md-editor";

import {useReduxData} from "../../../customHooks/useContextReduxData";
import {getCoordinatesForDescriptionWindow} from "../../../utils/figureCoordinatesForDescription";


export function ParametrizedDescription({title, text, x, y, showOrHide}) {
    const [maxHeight, setMaxHeight] = useState(y);

    const descriptionMenu = useRef(null);

    useEffect(() => {
        setMaxHeight(
            document.body.clientHeight - descriptionMenu?.current?.getBoundingClientRect().height
        );
    }, [title, text]);

    const animationName = (showOrHide === "hide") ? "hide" : "show";
    const speed = text !== "" ? "1s" : "2s";

    return <div className="description-window-invisible-container" style={{
        marginLeft: `clamp(0%, ${x - 250}px, calc(100% - 250px))`,
        marginTop: `clamp(0%, ${y}px, ${maxHeight}px)`
    }}>
        <div className="description-window" ref={descriptionMenu} style={{
            animationName: animationName,
            animationDuration: speed,
            overflowWrap: "anywhere",
            animationFillMode: "forwards"
        }}>
            <h4 className="description-window-title">{title}</h4>
            <div>
                <MDEditor.Markdown
                    source={text}
                    className="description-window-text"
                    style={{background: "transparent"}}
                />
            </div>
        </div>
    </div>;
}

export default function Description() {
    const {
        objectDescriptionParameters: [objectName, objectDescription, objectCenterX, objectCenterY]
    } = useReduxData();

    if (!objectName || !objectDescription) {
        return null;
    }

    return <ParametrizedDescription
        title={objectName}
        text={objectDescription}
        x={objectCenterX}
        y={objectCenterY}
    />;
}


export function WatchModeDescription({CANVAS_HEIGHT, CANVAS_WIDTH, canvasBoundingClientRect}) {
    const [state, dispatch] = useReducer(descriptionReducer, {});
    const {isWatchMode, presentState, scale} = useReduxData();

    useEffect(() => {
        if (
            isWatchMode
            && presentState?.get('watch')?.get('lastAction')?.type === 'changeObjectDescription'
        ) {
            const currentFigureUuid = presentState.get('watch').get('lastAction').uuid;
            const currentFigure = presentState.get('watch').get('figures').get(currentFigureUuid);
            let {x, y} = getCoordinatesForDescriptionWindow(
                currentFigure,
                scale,
                CANVAS_WIDTH,
                CANVAS_HEIGHT,
                canvasBoundingClientRect,
            );
            dispatch({
                type: "addWindow",
                payload: {
                    uuid: presentState.get('watch').get('lastAction').uuid,
                    name: currentFigure.name,
                    description: presentState.get('watch').get('lastAction').description,
                    x,
                    y,
                    isHiding: false,
                }
            });
        } else if (!isWatchMode) {
            dispatch({type: 'clearAll'});
        } else if (
            presentState?.get('watch')?.get('lastAction')?.type !== 'changeObjectDescription'
        ) {
            dispatch({type: 'setHidingToAll', payload: {isHiding: true}});

            // TODO: it may not work if a description already appeared within 1s
            let timer = setTimeout(() => dispatch({type: 'clearAll'}), 2000);
            return () => clearTimeout(timer);
        }
    }, [isWatchMode, presentState]);

    return (
        Object.keys(state).map(figureUuid =>
            <ParametrizedDescription
                title={state[figureUuid].name}
                text={state[figureUuid].description}
                x={state[figureUuid].x}
                y={state[figureUuid].y}
                showOrHide={state[figureUuid].isHiding ? "hide" : "show"}
            />
        )
    );
}


function descriptionReducer(state, action) {
    const payload = action.payload;

    switch (action.type) {
        case "clearAll":
            return {};
        case "addWindow": {
            let newState = {...state};
            newState[payload.uuid] = {
                name: payload.name,
                description: payload.description,
                x: payload.x,
                y: payload.y,
                isHiding: payload.isHiding,
            };
            return newState;
        }
        case "deleteWindow": {
            let newState = {...state};
            delete newState[payload.uuid];
            return newState;
        }
        case "setHiding": {
            let newState = {...state};
            newState[payload.uuid] = {...newState[payload.uuid], isHiding: payload.isHiding};
            return newState;
        }
        case "setHidingToAll": {
            let newState = {};

            let uuids = Object.keys(state);
            for (let i = 0; i < uuids.length; i++) {
                newState[uuids[i]] = {...state[uuids[i]], isHiding: payload.isHiding};
            }
            return newState;
        }
        case "setDescription": {
            let newState = {...state};
            newState[payload.uuid] = {...newState[payload.uuid], description: payload.description};
            return newState;
        }
        default:
            return state;
    }
}
